import { get, has, findIndex, find, isEmpty } from 'lodash';
import moment from 'moment';
import Platform from 'platform';
import requester from 'utils/requester';
import { parseChannelId, parseProgramStartTime } from 'utils/get-pathname-value';
import querySearchToObject from 'utils/query-search-to-object';
import storage from 'helpers/storage';
import api from 'config/api';
import config from 'config';
import i18n from 'i18n';
import { hideModal, showModal } from 'actions/modal';
import { saveVideoProgress, fetchDetails, fetchSeasons, fetchEpisodes, resetVideoProgress } from 'actions/details';
import { toggleVodObjectFavoriteStatus, fetchEpisodeDetails } from 'actions/details';
import {
  toggleChannelInFavoriteStatus,
  toggleProgramInFavoriteStatus,
  fetchChannels,
  fetchEpg,
  fetchExtraEPGItems,
} from 'actions/channels';
import { showEPG } from 'actions/epg-widget';
import {
  getIsContinueWatchingSelector,
  getIsStoppedStatusSelector,
  getPlayerTypeSelector,
  getVideoIdSelector,
  getCurrentTimeSelector,
  getErrorSelector,
  getSeriesIdSelector,
  getEpisodeIdSelector,
  getProgramStartTimeSelector,
  getChannelIdSelector,
} from 'reducers/player/selectors';

import { getVodTypeSelector } from 'reducers/details/selectors';
import { getChannelsDataSelector, getAvailableChannelsDataSelector } from 'reducers/channels/selectors';
import { getIsLoggedInSelector } from 'reducers/profile/selectors';

import PLAYER_TYPES from 'constants/player-types';

import * as types from './types';
import { MODAL_TYPES } from 'constants/modal-types';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import { resolvePlayerNavLink, getAuthorizedEndpointUrl } from 'helpers/urlhelper';
import { getMiddleValue } from 'utils/array';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';

const LAST_CHANNEL_KEY = 'lastChannel';

const VIDEO_EVENTS = {
  START: 'START',
  PLAYING: 'PLAYING',
  RESUME: 'RESUME',
  STOP: 'STOP',
};

const VIDEO_TYPES_EVENTS = {
  [PLAYER_TYPES.VOD]: 'VOD',
  [PLAYER_TYPES.SERIES]: 'VOD',
  [PLAYER_TYPES.CHANNEL]: 'CHANNEL',
  [PLAYER_TYPES.TIMESHIFT]: 'CHANNEL',
  [PLAYER_TYPES.CATCHUP]: 'CATCHUP',
  [PLAYER_TYPES.VOD_CHANNEL]: 'CATCHUP',
};

const STREAM_ERROR_TEXT = {
  NO_STREAMS_PURCHASED: {
    description: 'player.sbErrorTexts.description_NO_STREAMS_PURCHASED',
    button: 'player.sbErrorTexts.button_NO_STREAMS_PURCHASED',
  },
  PURCHASED_STREAMS_EXCEEDED: {
    description: 'player.sbErrorTexts.description_PURCHASED_STREAMS_EXCEEDED',
    button: 'player.sbErrorTexts.button_PURCHASED_STREAMS_EXCEEDED',
  },
  PURCHASABLE_STREAMS_EXCEEDED: {
    description: 'player.sbErrorTexts.description_PURCHASABLE_STREAMS_EXCEEDED',
  },
  DEFAULT_ERROR: {
    description: 'player.sbErrorTexts.description_DEFAULT_ERROR',
  },
};

let checkPlayingStatusInterval = null;

export const onPlayerOpen = (payload) => (dispatch) => {
  dispatch({ type: types.OPEN, payload });

  const isChannel = [
    PLAYER_TYPES.CATCHUP,
    PLAYER_TYPES.CHANNEL,
    PLAYER_TYPES.TIMESHIFT,
    PLAYER_TYPES.VOD_CHANNEL,
  ].includes(payload.playerType);

  if (isChannel) {
    dispatch(saveLastPlayedChannel());
  } else {
    dispatch(clearLastPlayedChannel());
  }
};

export const onPlayerClose = () => (dispatch) => {
  clearInterval(checkPlayingStatusInterval);
  dispatch({ type: types.CLOSE });
};

export const onStartPlaying = (currentTime) => async (dispatch, getState) => {
  try {
    const state = getState();
    const isContinueWatching = getIsContinueWatchingSelector(state);
    const isStopped = getIsStoppedStatusSelector(state);
    const playerType = getPlayerTypeSelector(state);
    const eventType = !isContinueWatching && !isStopped ? VIDEO_EVENTS.START : VIDEO_EVENTS.RESUME;

    dispatch({ type: types.ON_START_PLAYING });

    if (playerType !== PLAYER_TYPES.TRAILER) {
      dispatch(trackVideoEvent({ eventType, currentTime }));
    }
  } catch (error) {
    console.error(error);
  }
};

export const onStopPlaying = (currentTime) => async (dispatch, getState) => {
  try {
    const state = getState();
    const playerType = getPlayerTypeSelector(state);
    const hasPlayerError = !!getErrorSelector(state);

    // Если есть ошибка в плеере, то это техническая остановка плеера
    // и логгировать ивенты не нужно
    if (hasPlayerError) return;

    if ([PLAYER_TYPES.CHANNEL, PLAYER_TYPES.TIMESHIFT].includes(playerType)) return;

    const videoId = getVideoIdSelector(state);
    const seriesId = getSeriesIdSelector(state);
    const episodeId = getEpisodeIdSelector(state);

    // No need to save progress for these types
    if (![PLAYER_TYPES.CATCHUP, PLAYER_TYPES.TRAILER].includes(playerType)) {
      dispatch(saveVideoProgress({ videoId, seriesId, episodeId, currentTime }));
    }

    dispatch({ type: types.ON_STOP_PLAYING });
    if (playerType !== PLAYER_TYPES.TRAILER) {
      dispatch(trackVideoEvent({ eventType: VIDEO_EVENTS.STOP, currentTime }));
    }
  } catch (error) {
    console.error(error);
  }
};

export const onTimeUpdate = (payload) => ({ type: types.ON_TIME_UPDATE, payload });

export const trackVideoEvent =
  ({ eventType, currentTime }) =>
  async (dispatch, getState, history) => {
    const isVideoStartTypeOfEvent = [VIDEO_EVENTS.START, VIDEO_EVENTS.RESUME].includes(eventType);
    const state = getState();
    const playerType = getPlayerTypeSelector(state);
    const videoType = VIDEO_TYPES_EVENTS[playerType];

    try {
      let position = Math.floor(currentTime);
      let videoId = null;
      //необходимо для того, чтобы доставать vodType из стейта по ID сериала
      let seriesId = null;

      if (playerType === PLAYER_TYPES.CATCHUP || playerType === PLAYER_TYPES.VOD_CHANNEL) {
        const channelId = getChannelIdSelector(state);
        const channels = getChannelsDataSelector(state);
        const startTime = getProgramStartTimeSelector(state);
        const { epg = [] } = channels.find((channel) => channel.id === channelId) || {};
        const currentProgram = epg.find((program) => program.startTime === startTime) || {};
        videoId = currentProgram.id;
      } else if ([PLAYER_TYPES.CHANNEL, PLAYER_TYPES.TIMESHIFT].includes(playerType)) {
        videoId = getChannelIdSelector(state);
        position = 0;
      } else if (playerType === PLAYER_TYPES.SERIES) {
        videoId = getEpisodeIdSelector(state);
        seriesId = getSeriesIdSelector(state);
      } else {
        videoId = getVideoIdSelector(state);
      }

      if (!videoId) return;

      //Не смогла достучаться через селектор.... Если перезапускать со страницы плеера.

      /* Для каналов и EPG событий не возвращается параметр "visibilityVodType", поэтому для них
      пока хардкодим значение "vodType" = SVOD. Необходимо будет выпилить после доработок Azdio. */

      const vodType = ![
        PLAYER_TYPES.CHANNEL,
        PLAYER_TYPES.CATCHUP,
        PLAYER_TYPES.TIMESHIFT,
        PLAYER_TYPES.VOD_CHANNEL,
      ].includes(playerType)
        ? getVodTypeSelector(state, seriesId || videoId)
        : 'SVOD';

      const data = {
        deviceUuid: Platform.getUID(),
        position,
        videoId,
        videoType,
        eventType,
        vodType,
      };
      const { interval } = await requester({
        method: 'post',
        url: `${api.host}${api.endpoints.tracking}`,
        data,
      });
      if (isVideoStartTypeOfEvent) {
        const intervalMs = interval * 60 * 1000;
        clearInterval(checkPlayingStatusInterval);
        checkPlayingStatusInterval = setInterval(() => dispatch(trackVideoCurrentTime()), intervalMs);
      } else if (eventType === VIDEO_EVENTS.STOP) {
        clearInterval(checkPlayingStatusInterval);
      }
    } catch (error) {
      console.error(error);
      const errorCode = get(error.response, ['status']);
      const errorMessageCode = get(error.response, ['data', 'errorCode'], 'DEFAULT_ERROR');
      /*
       * Следует обратить внимание, что на события START и RESUME можно получить ответ с errorCode 403,
       * когда аутентифицированный пользователь достиг максимального кол-ва просмотров допустимого для него.
       * В данном случаем необходимо вывести пользователю соответствующее сообщение об ошибке и не играть стрим.
       */
      if (isVideoStartTypeOfEvent && errorCode === 403) {
        const message = i18n.t('player.maxCountOfViews.title');
        innerAnalyticsVolia.postEvent({
          eventGroup: 'notification',
          eventName: 'device limit reached',
          eventValue: 'show notification',
        });

        const modalTypeByError = STREAM_ERROR_TEXT[errorMessageCode].button ? MODAL_TYPES.CONFIRM : MODAL_TYPES.INFO;

        let modalConfig = {
          message: i18n.t(STREAM_ERROR_TEXT[errorMessageCode].description),
          buttonText: i18n.t('common.close'),
          onModalBack: () => {
            dispatch(hideModal());
            history.go(-1);
          },
        };

        if (modalTypeByError === MODAL_TYPES.CONFIRM) {
          modalConfig = {
            message: i18n.t(STREAM_ERROR_TEXT[errorMessageCode].description),
            confirmText: i18n.t(STREAM_ERROR_TEXT[errorMessageCode].button),
            rejectText: i18n.t('common.close'),
            onModalConfirm: () => {
              dispatch(hideModal());
              innerAnalyticsVolia.postEvent({
                eventGroup: 'notification',
                eventName: 'device limit reached',
                eventValue: 'click manage subscription',
              });

              const key = [PLAYER_TYPES.CHANNEL, PLAYER_TYPES.CATCHUP, PLAYER_TYPES.TIMESHIFT].includes(playerType)
                ? 'channelId'
                : 'vodAssetId';

              const id = key === 'channelId' ? getChannelIdSelector(state) : getVideoIdSelector(state);

              const search = `?${key}=${id}`;
              history.push({ pathname: '/profile/packages/filter', search });
            },
            onModalBack: () => {
              dispatch(hideModal());
              history.go(-1);
            },
          };
        }

        dispatch({ type: types.ERROR, payload: message });
        dispatch(showModal(modalTypeByError, modalConfig));
      }
    }
  };

export const trackVideoCurrentTime = () => (dispatch, getState) => {
  try {
    const currentTime = Math.floor(getCurrentTimeSelector(getState()));
    dispatch(trackVideoEvent({ eventType: VIDEO_EVENTS.PLAYING, currentTime }));
  } catch (error) {
    console.error(error);
  }
};

export const onEnded = () => (dispatch, getState) => {
  try {
    const state = getState();
    const playerType = getPlayerTypeSelector(state);

    if (
      [PLAYER_TYPES.CHANNEL, PLAYER_TYPES.TIMESHIFT, PLAYER_TYPES.CATCHUP, PLAYER_TYPES.VOD_CHANNEL].includes(
        playerType,
      )
    ) {
      return;
    }

    const videoId = getVideoIdSelector(state);
    const seriesId = getSeriesIdSelector(state);
    const episodeId = getEpisodeIdSelector(state);

    dispatch(resetVideoProgress({ videoId, seriesId, episodeId }));
  } catch (error) {
    console.error(error);
  }
};

export const onError = (payload) => (dispatch, _, history) => {
  dispatch(
    showModal(MODAL_TYPES.INFO, {
      message: payload,
      onModalBack() {
        dispatch(hideModal());
        history.go(-1);
      },
    }),
  );
  dispatch({ type: types.ERROR, payload });
};

export const toggleInFavoriteStatus = (payload) => (dispatch, getState) => {
  try {
    const { type, id, channelId } = payload;
    switch (type) {
      case PLAYER_TYPES.CHANNEL:
      case PLAYER_TYPES.TIMESHIFT: {
        dispatch(toggleChannelInFavoriteStatus(id));
        break;
      }
      case PLAYER_TYPES.CATCHUP: {
        dispatch(toggleProgramInFavoriteStatus({ id, channelId }));
        break;
      }
      case PLAYER_TYPES.SERIES: {
        const seriesId = getSeriesIdSelector(getState());
        dispatch(toggleVodObjectFavoriteStatus({ id: seriesId }));
        break;
      }
      // vod PLAYER_TYPES.VOD
      default: {
        dispatch(toggleVodObjectFavoriteStatus({ id: id }));
      }
    }
  } catch (error) {
    console.error(error);
  }
};

export const resolveTimeShiftPlayer = (item) => async (dispatch, getState) => {
  const state = getState();
  const isLoggedIn = getIsLoggedInSelector(state);
  const channels = getChannelsDataSelector(state);
  let currentChannel = find(channels, { id: item.id });
  let currentProgram = currentChannel?.currentProgram;
  let isDVR = !!currentChannel?.tvServices?.catchupTv;

  // Fetch channel and program to check if it's timeshift
  if (!currentChannel) {
    try {
      currentChannel = await requester({
        method: 'get',
        url: `${api.host}${getAuthorizedEndpointUrl(api.endpoints.channel, isLoggedIn)}/${item.id}`,
      });
      isDVR = !!currentChannel?.tvServices?.catchupTv;

      if (!isDVR) return null;

      const date = moment().add(config.timeDiffFromServer, 'm').toISOString();

      [currentProgram] = await requester({
        method: 'get',
        url: `${api.host}${getAuthorizedEndpointUrl(api.endpoints.epg(currentChannel.id, date, date), isLoggedIn)}`,
      });
    } catch (error) {
      console.error(error);
    }
  }

  const startTime = currentProgram?.startTime;

  return isDVR && startTime;
};

export const resolveVodChannel = (item) => async (_, getState) => {
  const state = getState();
  const isLoggedIn = getIsLoggedInSelector(state);
  const channels = getChannelsDataSelector(state);
  let currentChannel = find(channels, { id: item.id });

  if (!currentChannel) {
    try {
      currentChannel = await requester({
        method: 'get',
        url: `${api.host}${getAuthorizedEndpointUrl(api.endpoints.channel, isLoggedIn)}/${item.id}`,
        params: { includeVod: true },
      });
    } catch (error) {
      console.error(error);
    }
  }

  return !!currentChannel?.isVod;
};

export const navigateToPlayerPage = (payload) => async (dispatch, _, history) => {
  try {
    const { item, type, useReplace = false, query } = payload;
    const routerAction = useReplace ? history.replace : history.push;
    let timeshiftBegin = null;
    let isVodChannel = false;

    if (type === PLAYER_TYPES.CHANNEL) {
      timeshiftBegin = await dispatch(resolveTimeShiftPlayer(item));
      isVodChannel = await dispatch(resolveVodChannel(item));
    }

    let url = resolvePlayerNavLink(item, { type, query, timeshiftBegin, isVodChannel });
    if (!url) return;
    if (url.includes('vod-channel') && Platform.getDeviceType() === 'OLD_TV') {
      const lastFocusedElem = document.activeElement;
      const handleClose = () => {
        SpatialNavigationWrapper.focus(lastFocusedElem);
        dispatch(hideModal());
      };
      dispatch(
        showModal(MODAL_TYPES.INFO, {
          message: i18n.t('player.oldDevicesVodChannelError.text'),
          buttonText: i18n.t('player.oldDevicesVodChannelError.button'),
          onModalBack: handleClose,
        }),
      );
      return;
    }
    dispatch({ type: types.NAVIGATE_TO_PLAYER_PAGE, payload: { url, useReplace } });
    routerAction(url);
  } catch (error) {
    console.error(error);
  }
};

export const navigateToFirstAvailableChannel = () => (dispatch, getState) => {
  try {
    const availableChannels = getAvailableChannelsDataSelector(getState());
    const item = get(availableChannels, [0]);

    if (item) {
      dispatch(navigateToPlayerPage({ item, type: PLAYER_TYPES.CHANNEL, useReplace: true }));
    } else {
      dispatch(showEPG());
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * Отвечает за то, чтобы все нужные для работы плеера данные были загружены
 * Если данные есть - ничего не делает
 *
 * @param {string} playerType
 * @param {object} params
 */
export const loadPlayerData =
  ({ playerType, params }) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: types.LOAD_DATA });

      const { seriesId, videoId, episodeId, channelId, startTime } = params;

      switch (playerType) {
        case PLAYER_TYPES.VOD:
        case PLAYER_TYPES.TRAILER:
          const detailsObj = getState().details[videoId];
          if (!detailsObj) {
            await dispatch(fetchDetails(videoId, 'asset'));
          }
          break;
        case PLAYER_TYPES.SERIES: {
          const seriesObj = getState().details[seriesId];
          let seasonIndex = 0;

          if (!seriesObj) {
            await dispatch(fetchDetails(seriesId, 'tvSeries', false));
            await dispatch(fetchSeasons(seriesId, false));
          }

          const { details } = getState();

          if (episodeId) {
            const isLoggedIn = getIsLoggedInSelector(getState());
            const { metadata: episodeMetadata } = await fetchEpisodeDetails(episodeId, isLoggedIn);
            const currentSeasonId = episodeMetadata.tvSeasonId;
            if (currentSeasonId) {
              seasonIndex = findIndex(details[seriesId].data.seasons, { id: currentSeasonId });
            }
          }

          const season = details[seriesId].data.seasons[seasonIndex];
          // Вполне возможна ситуация, что сериал есть, но не загружены эпизоды
          // для сезона в котором находится серия (прямой переход с карусели на главной и тд)
          if (!season.episodes) {
            await dispatch(fetchEpisodes(season.vodAssetsUrl, seriesId, seasonIndex));
          }
          break;
        }
        case PLAYER_TYPES.CHANNEL:
        case PLAYER_TYPES.VOD_CHANNEL:
        case PLAYER_TYPES.CATCHUP:
        case PLAYER_TYPES.TIMESHIFT: {
          const channelsData = getChannelsDataSelector(getState());
          const currentChannel = find(channelsData, { id: channelId });

          // Sometimes "channelId" can be null
          // and we need to protect this from refetching all channels if they already fetched
          // (it deletes all programs)
          if (!currentChannel && isEmpty(channelsData)) await dispatch(fetchChannels());
          if (!has(currentChannel, ['epg']) || !find(currentChannel.epg, { startTime })) {
            await dispatch(fetchEpg({ channelId, startTime }));
          }

          if (!startTime && playerType === PLAYER_TYPES.VOD_CHANNEL) {
            const updatedChannel = find(getChannelsDataSelector(getState()), { id: channelId });
            const program = updatedChannel.currentProgram || getMiddleValue(updatedChannel.epg);
            const metadata = { startTime: program.startTime };
            dispatch(
              navigateToPlayerPage({
                item: { id: channelId, metadata },
                type: PLAYER_TYPES.CHANNEL,
                useReplace: true,
              }),
            );
            return;
          }

          break;
        }
        default:
          throw new Error('Unknow player type!');
      }

      dispatch({ type: types.LOAD_DATA_SUCCESS });
    } catch (error) {
      console.error(error);
      dispatch({ type: types.LOAD_DATA_ERROR, payload: { error: error.message } });
    }
  };

export const saveLastPlayedChannel = () => (dispatch, _, history) => {
  const { pathname, search } = history.location;

  const activeCategoryId = Number(querySearchToObject(search).activeCategoryId);
  const channelId = parseChannelId(pathname);
  const startTime = parseProgramStartTime(pathname);
  const timestamp = Date.now();

  const data = { activeCategoryId, channelId, startTime, timestamp };

  storage.set(LAST_CHANNEL_KEY, JSON.stringify(data));
};

export const clearLastPlayedChannel = () => () => storage.remove(LAST_CHANNEL_KEY);

export const navigateToLastPlayedChannel = () => (dispatch) => {
  try {
    const data = JSON.parse(storage.get(LAST_CHANNEL_KEY));

    if (!data) return;

    const { channelId, startTime, activeCategoryId, timestamp } = data;
    const hoursPassed = moment(Date.now()).diff(moment(timestamp), 'hours');

    if (hoursPassed > 24) return;

    const metadata = { startTime };
    const query = { activeCategoryId };
    const item = { id: channelId, metadata };

    dispatch(navigateToPlayerPage({ item, type: PLAYER_TYPES.CHANNEL, query }));
  } catch (error) {
    console.error(error);
  }
};

export const changeProgram = (nextProgram) => async (dispatch, getState) => {
  if (!nextProgram) return;

  const { isLive, channelId, startTime } = nextProgram || {};
  const item = { id: channelId };

  if (!isLive) item.metadata = { startTime };

  dispatch(navigateToPlayerPage({ item, type: PLAYER_TYPES.CHANNEL, useReplace: true }));

  // Load extra epg if needed
  // https://hootko.atlassian.net/browse/STVWL-600
  const threshold = 5;
  const channels = getChannelsDataSelector(getState());
  const epg = channels.find(({ id }) => id === channelId)?.epg || [];
  const index = epg.findIndex((item) => item.startTime === startTime);
  const epgSize = epg?.length || 0;

  let direction;

  if (index + threshold >= epgSize) {
    direction = 'next';
  } else if (index <= threshold) {
    direction = 'prev';
  }

  if (direction) dispatch(fetchExtraEPGItems({ channelId, direction }));
};
